import React, { useEffect, useRef, useState } from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import CircularProgress from "@material-ui/core/CircularProgress";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import RefreshIcon from "@material-ui/icons/Refresh";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { useDialogStyles } from "../../theme/redesign";
import { formatDateTime } from "./helpers";
import { tNegocio } from "./shared";

const copyText = async (value) => {
  try {
    await navigator.clipboard.writeText(String(value || ""));
    toast.success(tNegocio("pages.binance.copied"));
  } catch {
    toast.error(tNegocio("pages.binance.copyFail"));
  }
};

const PAY_EVENT = "negocio:binance-payment";

/** Abre la validación de pago Binance tras enviar los datos al cliente (respuesta de /negocio/notify). */
export const openBinancePaymentFromNotify = (data) => {
  const payment = data?.payment;
  if (!payment?.saleId || !payment?.checkout) return;
  if (payment.checkout.status === "paid") return;
  window.dispatchEvent(new CustomEvent(PAY_EVENT, { detail: payment }));
};

export function BinancePaymentHost() {
  const [payment, setPayment] = useState(null);
  useEffect(() => {
    const onPay = (e) => setPayment(e.detail || null);
    window.addEventListener(PAY_EVENT, onPay);
    return () => window.removeEventListener(PAY_EVENT, onPay);
  }, []);
  return (
    <BinanceCheckoutDialog
      open={!!payment}
      saleId={payment?.saleId}
      checkout={payment?.checkout}
      onClose={() => setPayment(null)}
      onPaid={() => setTimeout(() => setPayment(null), 1500)}
    />
  );
}

export default function BinanceCheckoutDialog({ open, saleId, checkout: initial, onClose, onPaid }) {
  const dlg = useDialogStyles();
  const [checkout, setCheckout] = useState(initial || null);
  const [orderId, setOrderId] = useState("");
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const timer = useRef(null);

  const refresh = async (silent = false) => {
    if (!saleId) return;
    if (!silent) setLoading(true);
    try {
      const { data } = await api.post(`/negocio/sales/${saleId}/binance-poll`);
      setCheckout(data);
      if (data?.status === "paid") {
        toast.success(tNegocio("pages.binance.paid"));
        onPaid?.(data);
      }
    } catch (e) {
      if (!silent) toastError(e);
      try {
        const { data } = await api.get(`/negocio/sales/${saleId}/binance-checkout`);
        setCheckout(data);
      } catch (err) {
        if (!silent) toastError(err);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return undefined;
    setCheckout(initial || null);
    setOrderId("");
    if (saleId && !initial) refresh(true);
    timer.current = setInterval(() => refresh(true), 15000);
    return () => {
      if (timer.current) clearInterval(timer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, saleId]);

  const confirmOrder = async () => {
    if (!saleId || !orderId.trim()) return;
    setConfirming(true);
    try {
      const { data } = await api.post(`/negocio/sales/${saleId}/binance-confirm`, {
        orderId: orderId.trim(),
      });
      setCheckout(data);
      toast.success(tNegocio("pages.binance.paid"));
      onPaid?.(data);
    } catch (e) {
      toastError(e);
    } finally {
      setConfirming(false);
    }
  };

  const amount = checkout?.amountUsdt != null ? Number(checkout.amountUsdt).toFixed(2) : "—";
  const paid = checkout?.status === "paid";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth classes={{ paper: dlg.paper }}>
      <DialogTitle>{tNegocio("pages.binance.checkoutTitle")}</DialogTitle>
      <DialogContent dividers>
        {loading && !checkout ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
            <CircularProgress size={28} />
          </div>
        ) : (
          <>
            <div style={{ textAlign: "center", marginBottom: 16 }}>
              {checkout?.qrUrl ? (
                <img
                  src={checkout.qrUrl}
                  alt="QR Binance Pay"
                  style={{ width: 180, height: 180, objectFit: "contain", borderRadius: 8 }}
                />
              ) : null}
              <div style={{ fontSize: 28, fontWeight: 800, marginTop: 12 }}>
                {amount} {checkout?.currency || "USDT"}
              </div>
              <div style={{ opacity: 0.75, marginTop: 4 }}>
                {tNegocio("pages.binance.exactAmount")}
              </div>
            </div>
            {checkout?.payId ? (
              <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12 }}>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  label={tNegocio("pages.binance.payId")}
                  value={checkout.payId}
                  InputProps={{ readOnly: true }}
                />
                <Button size="small" onClick={() => copyText(checkout.payId)} startIcon={<FileCopyIcon />}>
                  {tNegocio("pages.binance.copy")}
                </Button>
              </div>
            ) : null}
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 16 }}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                label={tNegocio("pages.binance.amount")}
                value={`${amount} USDT`}
                InputProps={{ readOnly: true }}
              />
              <Button size="small" onClick={() => copyText(amount)} startIcon={<FileCopyIcon />}>
                {tNegocio("pages.binance.copy")}
              </Button>
            </div>
            {paid ? (
              <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#2e7d32", fontWeight: 700 }}>
                <CheckCircleIcon />
                {tNegocio("pages.binance.paid")}
                {checkout.paidAt ? ` · ${formatDateTime(checkout.paidAt)}` : ""}
              </div>
            ) : (
              <>
                <div style={{ opacity: 0.8, fontSize: 13, marginBottom: 12 }}>
                  {tNegocio("pages.binance.checkoutHint")}
                  {checkout?.expiresAt
                    ? ` ${tNegocio("pages.binance.expires").replace("{date}", formatDateTime(checkout.expiresAt))}`
                    : ""}
                </div>
                <TextField
                  fullWidth
                  margin="dense"
                  variant="outlined"
                  label={tNegocio("pages.binance.orderId")}
                  value={orderId}
                  onChange={(e) => setOrderId(e.target.value)}
                  helperText={tNegocio("pages.binance.orderIdHint")}
                />
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{tNegocio("close")}</Button>
        {!paid ? (
          <>
            <Button startIcon={<RefreshIcon />} onClick={() => refresh(false)} disabled={loading}>
              {tNegocio("pages.binance.refresh")}
            </Button>
            <Button
              color="primary"
              variant="contained"
              onClick={confirmOrder}
              disabled={confirming || !orderId.trim()}
            >
              {confirming ? <CircularProgress size={18} color="inherit" /> : tNegocio("pages.binance.confirmOrder")}
            </Button>
          </>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}
